import type { FantasyContest, FantasyStandings, FantasyTeam } from '@brush/api-client';
import { ErrorRetry, Spinner, formatPoints, formatSalary } from '@brush/ui';
import { useState } from 'react';
import {
  budgetUnit,
  contestStatusOf,
  formatInstant,
  memberNames,
  withUnit,
  type ContestStatus,
} from '../../fantasy/contest';
import { useFantasyPool, useFantasyStandings, useFantasyTeams } from '../../queries';
import { ExportControl } from '../ExportControl';

/**
 * Who has entered, and — once scoring starts — how they are doing.
 *
 * <b>Not part of the numbered builder.</b> The six sections are the contest's *design*, read top to
 * bottom while it is being set up; this is what the contest has *become*, and it only exists once
 * somebody has entered. Numbering it seven would put a section on the screen that says "nothing
 * yet" for the entire time the sections above it are being worked on.
 *
 * <b>Standings are only asked for from Locked onwards</b>, which is where §6 puts the first
 * snapshot. Before that the endpoint answers an empty list with a null sequence number — a request
 * spent to be told what the status already said.
 *
 * <b>An operator reads; they do not edit.</b> A roster is the entrant's own act, and the API
 * refuses an edit on somebody else's team to everybody, administrators included — so there is
 * deliberately no control here that would suggest otherwise. There is also no way to remove a rogue
 * entry, which is a real gap and a recorded one rather than an oversight.
 */
export function ContestEntriesPanel({ contest }: { contest: FantasyContest }) {
  const status = contestStatusOf(contest);

  const teamsQ = useFantasyTeams(contest.id);
  // Members are named from the contest's own pool — every one of them is a pool player — rather than
  // the room's whole roster (performance rank 2).
  const poolQ = useFantasyPool(contest.id);
  const standingsQ = useFantasyStandings(contest.id, hasStandings(status));

  const [tab, setTab] = useState<'entries' | 'standings'>(
    hasStandings(status) ? 'standings' : 'entries',
  );

  const teams = teamsQ.data ?? [];
  const nameOf = memberNames(poolQ.data);

  return (
    <section className="mt-6 border border-line bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
        <h3 className="font-display text-sm font-semibold text-ink-950">
          {hasStandings(status) ? 'Entries and standings' : 'Entries'}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
        {/* F1 on the standings tab, F2 on the rosters (docs/specs/export.md EX5, EX7): the file is the tab on screen. */}
        {tab === 'standings' && hasStandings(status) ? (
          <ExportControl
            key="F1"
            availability={{ view: 'F1', status }}
            target={{ view: 'F1', contestId: contest.id }}
            viewLabel={`${contest.name} — standings`}
            subject={`${contest.name} standings`}
          />
        ) : (
          <ExportControl
            key="F2"
            availability={{ view: 'F2' }}
            target={{ view: 'F2', contestId: contest.id }}
            viewLabel={`${contest.name} — rosters`}
            subject={`${contest.name} rosters`}
          />
        )}
        {hasStandings(status) ? (
          <div role="group" aria-label="What to show" className="inline-flex border border-line p-0.5">
            {(['standings', 'entries'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={tab === value}
                onClick={() => setTab(value)}
                className={`px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] transition ${
                  tab === value ? 'bg-felt-700 text-white' : 'text-ink-800/70 hover:text-ink-950'
                }`}
              >
                {value === 'standings' ? 'Standings' : 'Rosters'}
              </button>
            ))}
          </div>
        ) : null}
        </div>
      </header>

      {tab === 'standings' && hasStandings(status) ? (
        standingsQ.isLoading ? (
          <div className="p-5">
            <Spinner label="Loading standings…" />
          </div>
        ) : standingsQ.isError ? (
          <div className="p-5">
            <ErrorRetry
              message="Couldn't load the standings."
              onRetry={() => void standingsQ.refetch()}
            />
          </div>
        ) : (
          <Standings standings={standingsQ.data!} contest={contest} />
        )
      ) : teamsQ.isLoading || (poolQ.isLoading && teams.length > 0) ? (
        <div className="p-5">
          <Spinner label="Loading entries…" />
        </div>
      ) : teamsQ.isError || (poolQ.isError && teams.length > 0) ? (
        <div className="p-5">
          <ErrorRetry
            message="Couldn't load the entries."
            onRetry={() => void (teamsQ.isError ? teamsQ.refetch() : poolQ.refetch())}
          />
        </div>
      ) : (
        <Rosters teams={teams} nameOf={nameOf ?? (() => 'A player at this room')} contest={contest} status={status} />
      )}
    </section>
  );
}

function Standings({
  standings,
  contest,
}: {
  standings: FantasyStandings;
  contest: FantasyContest;
}) {
  if (standings.data.length === 0) {
    return (
      <p className="px-5 py-8 text-center text-[12.5px] leading-relaxed text-ink-800/55">
        {/* Two different facts, worded apart: nothing has been computed, versus nobody entered. The
            first sends an operator to the leaderboard, the second nowhere. */}
        {standings.sequenceNumber === null
          ? 'Nothing has been scored yet. Standings appear the first time the leaderboard behind this contest recalculates.'
          : 'No teams were entered, so there is nothing to rank.'}
      </p>
    );
  }

  return (
    <>
      <p className="border-b border-line px-5 py-2 text-[11px] text-ink-800/55">
        {/* Read from a frozen snapshot rather than computed, so "as of" is not decoration — a
            board can be days behind and stale standings look exactly like current ones. */}
        As of {formatInstant(standings.computedAt)}
        {standings.isFinal ? ' · final' : ''}
        {/* §6.2 and §7.2. On the standings themselves rather than only on the contest header,
            because this is the thing it is a fact about — the same argument that put the pause on
            the contest rather than leaving it to the admin-only drift route. */}
        {standings.amendedAfterLock ? ' · amended after lock' : ''}
      </p>
      <table className="w-full text-left text-sm">
        <thead className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
          <tr className="border-b border-line">
            <th className="w-12 px-5 py-2 text-right font-semibold">#</th>
            <th className="px-2 py-2 font-semibold">Team</th>
            <th className="px-2 py-2 text-right font-semibold">Scoring</th>
            <th className="px-5 py-2 text-right font-semibold">Points</th>
          </tr>
        </thead>
        <tbody>
          {standings.data.map((row) => (
            <tr key={row.teamId} className="border-b border-line/60 last:border-b-0">
              {/* The snapshot's own rank, never the row index: tied teams share a rank and the
                  next one skips (1, 2, 2, 4). */}
              <td className="num px-5 py-1.5 text-right text-ink-800/60">
                {row.rank}
                {row.isTied ? <span className="text-ink-800/40">=</span> : null}
              </td>
              <td className="px-2 py-1.5 text-ink-950">{row.teamName}</td>
              <td className="num px-2 py-1.5 text-right text-ink-800/60">
                {row.membersScoring} of {contest.rosterSize}
              </td>
              <td className="num px-5 py-1.5 text-right font-semibold text-ink-950">
                {formatPoints(row.points)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Rosters({
  teams,
  nameOf,
  contest,
  status,
}: {
  teams: readonly FantasyTeam[];
  /** A member's name, from the contest's pool (`memberNames`). */
  nameOf: (playerId: string) => string;
  contest: FantasyContest;
  status: ContestStatus;
}) {
  const unit = budgetUnit(contest);

  if (teams.length === 0) {
    return (
      <p className="px-5 py-8 text-center text-[12.5px] leading-relaxed text-ink-800/55">
        {status === 'draft'
          ? 'Nobody can enter until this contest publishes.'
          : status === 'open'
            ? 'Nobody has entered yet. Entrants build a roster from the player portal.'
            : 'This contest closed with no entries.'}
      </p>
    );
  }

  return (
    <ul className="divide-y divide-line">
      {teams.map((team) => (
        <li key={team.id} className="px-5 py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="font-medium text-ink-950">{team.name}</span>
            <span className="num text-[11px] text-ink-800/55">
              {withUnit(team.salarySpent, unit)} of {withUnit(contest.budget, unit)} ·{' '}
              {team.members.length}/{contest.rosterSize}
              {/* The server's own verdict, not a count re-checked here. A roster can be short or
                  over budget only in states the write refuses, so this reads as a fault when it
                  appears — which is the point of showing it. */}
              {team.isValid ? '' : ' · not a legal roster'}
            </span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {team.members.map((member) => (
              <span
                key={member.playerId}
                className="border border-line px-2 py-0.5 text-[11px] text-ink-800/80"
              >
                {nameOf(member.playerId)}
                {member.role === 'captain' ? ' (C)' : ''}
                <span className="num ml-1.5 text-ink-800/45">
                  {formatSalary(member.salaryAtDraft)}
                </span>
              </span>
            ))}
          </div>
          {team.lockedAt ? (
            <p className="mt-1 text-[10.5px] text-ink-800/45">
              Locked {formatInstant(team.lockedAt)}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Ranks exist from the lock onwards — §6 puts the first snapshot there. */
function hasStandings(status: ContestStatus): boolean {
  return status === 'locked' || status === 'closed';
}
